/**
 * Portal ICAVI - Serviço de domínio: Calendário de Eventos
 * Extraído de apiService.ts para reduzir o tamanho do módulo monolítico (RF-07).
 *
 * Inclui o estado de módulo (cache, fila de requisições) que era mantido em
 * apiService.ts para throttling de chamadas ao endpoint de evento por data.
 */
import { api } from '../apiClient';
import { connectivityGuard } from '../connectivityGuard';
// Estado de módulo — cache e throttling para getEventoCalendarioPorData
const CALENDARIO_EVENTO_CACHE_TTL_MS = 5 * 60 * 1000;
const CALENDARIO_EVENTO_MIN_GAP_MS = 800;
const calendarioEventoCache = new Map();
const calendarioEventoInFlight = new Map();
let calendarioEventoLastRequestAt = 0;
let calendarioEventoQueue = Promise.resolve();
const aguardar = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const enfileirarCalendarioEvento = async (fn) => {
    const executar = async () => {
        const agora = Date.now();
        const espera = CALENDARIO_EVENTO_MIN_GAP_MS - (agora - calendarioEventoLastRequestAt);
        if (espera > 0) {
            await aguardar(espera);
        }
        calendarioEventoLastRequestAt = Date.now();
        return fn();
    };
    const promessa = calendarioEventoQueue.then(executar, executar);
    calendarioEventoQueue = promessa.then(() => undefined).catch(() => undefined);
    return promessa;
};
export const calendarioServiceMethods = {
    async listarEventosCalendario(incluirPassados = true) {
        try {
            const response = await api.get('/api/calendario-eventos', {
                params: { incluir_passados: incluirPassados },
            });
            const payload = response.data.dados ?? response.data.data;
            return payload?.eventos ?? [];
        }
        catch (error) {
            console.error('Erro ao listar eventos do calendário:', error);
            throw error;
        }
    },
    async getEventoCalendarioPorData(data, pagina) {
        const cacheKey = `${data}::${pagina ?? ''}`;
        const agora = Date.now();
        const cached = calendarioEventoCache.get(cacheKey);
        if (cached) {
            if (agora - cached.timestamp < CALENDARIO_EVENTO_CACHE_TTL_MS) {
                return cached.data;
            }
            calendarioEventoCache.delete(cacheKey);
        }
        const inFlight = calendarioEventoInFlight.get(cacheKey);
        if (inFlight) {
            return inFlight;
        }
        const requestPromise = enfileirarCalendarioEvento(async () => {
            try {
                const response = await api.get(`/api/calendario-eventos/data/${data}`, {
                    params: pagina ? { pagina } : undefined,
                });
                const payload = response.data.dados ?? response.data.data ?? null;
                calendarioEventoCache.set(cacheKey, { data: payload, timestamp: Date.now() });
                return payload;
            }
            catch (error) {
                if (!connectivityGuard.isExpectedTransient(error)) {
                    console.error('Erro ao consultar evento por data:', error);
                }
                if (cached) {
                    return cached.data;
                }
                throw error;
            }
            finally {
                calendarioEventoInFlight.delete(cacheKey);
            }
        });
        calendarioEventoInFlight.set(cacheKey, requestPromise);
        return requestPromise;
    },
    async criarEventoCalendario(evento) {
        try {
            const response = await api.post('/api/calendario-eventos', evento);
            return response.data;
        }
        catch (error) {
            console.error('Erro ao criar evento do calendário:', error);
            throw error;
        }
    },
    async atualizarEventoCalendario(eventoId, evento) {
        try {
            const response = await api.put(`/api/calendario-eventos/${eventoId}`, evento);
            return response.data;
        }
        catch (error) {
            console.error('Erro ao atualizar evento do calendário:', error);
            throw error;
        }
    },
    async excluirEventoCalendario(eventoId) {
        try {
            const response = await api.delete(`/api/calendario-eventos/${eventoId}`);
            return response.data;
        }
        catch (error) {
            console.error('Erro ao excluir evento do calendário:', error);
            throw error;
        }
    },
    async getNotificacoesEventosLogin() {
        try {
            const response = await api.get('/api/calendario-eventos/notificacoes', { params: { pagina: 'login' } });
            const payload = response.data.dados ?? response.data.data;
            return payload?.notificacoes ?? [];
        }
        catch (error) {
            console.error('Erro ao carregar notificações de eventos na tela de login:', error);
            throw error;
        }
    },
    async getSetoresColaboradores() {
        try {
            const response = await api.get('/api/colaboradores/setores');
            return response.data;
        }
        catch (error) {
            console.error('Erro ao buscar setores dos colaboradores:', error);
            throw error;
        }
    },
    async verificarPermissaoSetor(setorColaborador) {
        try {
            const userData = localStorage.getItem('userData');
            const user = userData ? JSON.parse(userData) : {};
            const response = await api.post('/api/colaboradores/verificar-permissao', {
                username: user.username,
                setor_colaborador: setorColaborador,
            });
            return response.data;
        }
        catch (error) {
            console.error('Erro ao verificar permissão:', error);
            throw error;
        }
    },
};
